<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1>
          <font-awesome-icon icon="balance-scale"/> {{ $t('pageTitle') }}
        </h1>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <div class="tile">
          <div class="form-inline">
            <label class="mr-1 mb-1">{{ $t('period') }}</label>
            <date-picker v-model="period" type="month" value-type="YYYY-MM" format="MM/YYYY"
                         :lang="$i18n.locale" :clearable="false" :placeholder="$t('selectMonth')"
                         class="mr-2 mb-1"></date-picker>
            <label class="mr-1 mb-1">{{ $t('provider') }}</label>
            <b-form-select class="mr-2 mb-1" v-model="gateway" :options="gatewayOptions" style="width: 220px"></b-form-select>
            <button @click="loadPivot" class="btn btn-primary mr-1 mb-1" :disabled="loading">
              <font-awesome-icon icon="sync" :spin="loading"/>&nbsp;{{ $t('loadReport') }}
            </button>
            <label class="btn btn-outline-success is-border-1 mr-1 mb-1 mb-0" :class="{disabled: importing || !period}">
              <font-awesome-icon icon="file-upload"/>&nbsp;{{ $t('importProviderFile') }}
              <input type="file" accept=".xlsx" hidden @change="onFile" :disabled="importing || !period"/>
            </label>
          </div>
          <b-alert show variant="info" class="small mt-2 mb-0">{{ $t('reconcileNote') }}</b-alert>
        </div>
      </div>

      <!-- Băng tổng hợp khi đã import file provider -->
      <div class="col-12" v-if="saved">
        <div class="row">
          <div class="col-md-3 col-6">
            <div class="widget-small primary tile"><div class="info">
              <h4>{{ $t('systemRevenue') }}</h4><p><b>{{ saved.systemTotals.revenue | numFormat('0,0') }}đ</b></p>
              <small>{{ saved.systemTotals.billableCount | numFormat('0,0') }} {{ $t('billableMsg') }}</small>
            </div></div>
          </div>
          <div class="col-md-3 col-6">
            <div class="widget-small warning tile"><div class="info">
              <h4>{{ $t('providerPayable') }}</h4><p><b>{{ saved.providerTotals.payable | numFormat('0,0') }}đ</b></p>
              <small>{{ saved.providerTotals.billableCount | numFormat('0,0') }} {{ $t('billableMsg') }}</small>
            </div></div>
          </div>
          <div class="col-md-3 col-6">
            <div class="widget-small tile" :class="margin >= 0 ? 'info' : 'danger'"><div class="info">
              <h4>{{ $t('grossMargin') }}</h4><p><b>{{ margin | numFormat('0,0') }}đ</b></p>
              <small>{{ $t('marginNote') }}</small>
            </div></div>
          </div>
          <div class="col-md-3 col-6">
            <div class="widget-small tile" :class="totalDiffCount === 0 ? 'info' : 'danger'"><div class="info">
              <h4>{{ $t('qtyDiff') }}</h4><p><b>{{ totalDiffCount | numFormat('+0,0') }}</b></p>
              <small>{{ totalDiffCount === 0 ? $t('matched') : $t('needReview') }}</small>
            </div></div>
          </div>
        </div>
      </div>

      <!-- Bảng đối soát (đã import) -->
      <div class="col-12" v-if="saved">
        <div class="c-smart-table tile">
          <div class="row c-smart-table__title">
            <div class="col-lg-3">{{ $t('oa') }}</div>
            <div class="col-lg-2">{{ $t('templateId') }}</div>
            <div class="col-lg-2 text-right">{{ $t('systemCount') }}</div>
            <div class="col-lg-2 text-right">{{ $t('providerCount') }}</div>
            <div class="col-lg-1 text-right">{{ $t('diff') }}</div>
            <div class="col-lg-2 text-right">{{ $t('systemRevenueShort') }}</div>
          </div>
          <div class="row" v-for="(l, i) in saved.lines" :key="i" :class="{'bg-danger-soft': l.diffCount !== 0}">
            <div class="col-lg-3">
              <span>{{ l.oaName }}</span>
              <span v-if="l.source === 'providerOnly'" class="badge badge-warning ml-1">{{ $t('providerOnly') }}</span>
              <span v-if="l.source === 'systemOnly'" class="badge badge-info ml-1">{{ $t('systemOnly') }}</span>
              <p class="text-gray-999 small mb-0" v-if="l.templateName">{{ l.templateName }}</p>
            </div>
            <div class="col-lg-2">{{ l.templateRefId || '—' }}</div>
            <div class="col-lg-2 text-right">{{ l.systemCount | numFormat('0,0') }}</div>
            <div class="col-lg-2 text-right">{{ l.providerCount | numFormat('0,0') }}</div>
            <div class="col-lg-1 text-right" :class="l.diffCount === 0 ? 'text-success' : 'text-danger font-weight-bold'">
              {{ l.diffCount | numFormat('+0,0') }}
            </div>
            <div class="col-lg-2 text-right">
              {{ l.systemRevenue | numFormat('0,0') }}
              <button class="btn btn-sm btn-link p-0 ml-1" v-if="l.templateRefId" @click="drill(l)" :title="$t('viewDetail')">
                <font-awesome-icon icon="search"/>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Pivot tự sinh từ DB (chưa import file) -->
      <div class="col-12" v-if="!saved && pivotLines.length">
        <b-alert show variant="secondary" class="small">{{ $t('pivotOnlyNote') }}</b-alert>
        <div class="c-smart-table tile">
          <div class="row c-smart-table__title">
            <div class="col-lg-4">{{ $t('oa') }}</div>
            <div class="col-lg-3">{{ $t('templateId') }}</div>
            <div class="col-lg-2 text-right">{{ $t('systemCount') }}</div>
            <div class="col-lg-3 text-right">{{ $t('systemRevenueShort') }}</div>
          </div>
          <div class="row" v-for="(l, i) in pivotLines" :key="i">
            <div class="col-lg-4">{{ l.oaName }}<p class="text-gray-999 small mb-0" v-if="l.templateName">{{ l.templateName }}</p></div>
            <div class="col-lg-3">{{ l.templateRefId || '—' }}</div>
            <div class="col-lg-2 text-right">{{ l.systemCount | numFormat('0,0') }}</div>
            <div class="col-lg-3 text-right">{{ l.systemRevenue | numFormat('0,0') }}đ</div>
          </div>
          <div class="row c-smart-table__title">
            <div class="col-lg-7"><b>{{ $t('total') }}</b></div>
            <div class="col-lg-2 text-right"><b>{{ pivotTotals.billableCount | numFormat('0,0') }}</b></div>
            <div class="col-lg-3 text-right"><b>{{ pivotTotals.revenue | numFormat('0,0') }}đ</b></div>
          </div>
        </div>
      </div>

      <div class="col-12 text-center text-danger" v-if="!saved && !pivotLines.length && loaded">
        {{ $t('noData') }}
      </div>
    </div>

    <!-- Modal drill-down từng tin -->
    <b-modal v-model="showDrill" :title="drillTitle" size="lg" hide-footer>
      <div class="text-center" v-if="drillLoading"><font-awesome-icon icon="spinner" spin/></div>
      <table class="table table-sm" v-else>
        <thead><tr><th>{{ $t('phone') }}</th><th>{{ $t('client') }}</th><th>{{ $t('sendTime') }}</th><th class="text-right">{{ $t('fee') }}</th></tr></thead>
        <tbody>
          <tr v-for="z in drillItems" :key="z._id">
            <td>{{ z.phone }}</td>
            <td>{{ z.client ? z.client.companyName : '—' }}</td>
            <td>{{ z.sendTime | moment('DD/MM/YY HH:mm') }}</td>
            <td class="text-right">{{ z.chargedFee | numFormat('0,0') }}đ</td>
          </tr>
        </tbody>
      </table>
      <div class="text-center text-gray-999 small" v-if="!drillLoading && drillCount > drillItems.length">
        {{ $t('showingFirst', [drillItems.length, drillCount]) }}
      </div>
    </b-modal>
  </main>
</template>

<script>
import Excel from 'exceljs';
import moment from 'moment';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import 'vue2-datepicker/locale/vi';

export default {
  components: {DatePicker},
  data() {
    return {
      period: moment().format('YYYY-MM'),
      gateway: '',
      gateways: [],
      loading: false,
      loaded: false,
      importing: false,
      pivotLines: [],
      pivotTotals: {billableCount: 0, revenue: 0},
      saved: null,
      showDrill: false,
      drillLoading: false,
      drillItems: [],
      drillCount: 0,
      drillTitle: '',
    };
  },
  computed: {
    gatewayOptions() {
      return [{value: '', text: this.$t('allProviders')}, ...this.gateways.map(g => ({value: g._id, text: g.name}))];
    },
    margin() {
      if (!this.saved) return 0;
      return (this.saved.systemTotals.revenue || 0) - (this.saved.providerTotals.payable || 0);
    },
    totalDiffCount() {
      if (!this.saved) return 0;
      return this.saved.lines.reduce((s, l) => s + (l.diffCount || 0), 0);
    },
  },
  mounted() {
    this.loadGateways();
  },
  methods: {
    async loadGateways() {
      try {
        const {data} = await this.$service.get('/zns-gateway', {params: {pageSize: 100}});
        this.gateways = (data && data.docs) || [];
      } catch (e) {
        this.gateways = [];
      }
    },
    async loadPivot() {
      if (!this.period) return;
      const loader = this.$loading.show();
      this.loading = true;
      try {
        const {data} = await this.$service.get('/zns-reconcile/pivot', {params: {period: this.period, gateway: this.gateway}});
        const d = data.data || {};
        this.pivotLines = d.lines || [];
        this.pivotTotals = d.totals || {billableCount: 0, revenue: 0};
        this.saved = d.saved || null;
        this.loaded = true;
      } catch (e) {
        this.$swal({text: (e.response && e.response.data && e.response.data.message) || 'Lỗi tải báo cáo', icon: 'error'});
      } finally {
        this.loading = false;
        loader.hide();
      }
    },
    async onFile(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = '';
      if (!file) return;
      const loader = this.$loading.show();
      this.importing = true;
      try {
        const buffer = await file.arrayBuffer();
        const parsed = await this.parseProviderFile(buffer);
        if (!parsed.providerLines.length) {
          throw new Error(this.$t('parseNoLines'));
        }
        const {data} = await this.$service.post('/zns-reconcile/import', {
          period: this.period,
          gateway: this.gateway,
          importedFileName: file.name,
          providerTotals: parsed.providerTotals,
          providerLines: parsed.providerLines,
        });
        this.saved = data.data;
        this.loaded = true;
        this.$swal({text: this.$t('importSuccess'), icon: 'success', timer: 1500, showConfirmButton: false});
      } catch (err) {
        this.$swal({text: (err.response && err.response.data && err.response.data.message) || err.message || 'Lỗi import file', icon: 'error'});
      } finally {
        this.importing = false;
        loader.hide();
      }
    },
    // Chuẩn hoá giá trị 1 cell exceljs về primitive. KHÔNG String(cell) trực tiếp —
    // cell gộp (merged) sẽ ném lỗi ở MergeValue.toString. Cell công thức trả {result},
    // cell rich-text/hyperlink trả {text}.
    cellRaw(cell) {
      if (cell == null) return null;
      const v = cell.value;
      if (v == null) return null;
      if (typeof v === 'object') {
        if (v.result != null) return v.result;
        if (v.text != null) return v.text;
        if (v.richText) return v.richText.map(r => r.text).join('');
        return null;
      }
      return v;
    },
    cellNum(cell) {
      const v = this.cellRaw(cell);
      if (typeof v === 'number') return v;
      if (v == null) return 0;
      const n = parseFloat(String(v).replace(/[^\d.-]/g, ''));
      return isNaN(n) ? 0 : n;
    },
    cellStr(cell) {
      const v = this.cellRaw(cell);
      return v == null ? '' : String(v).trim();
    },
    async parseProviderFile(buffer) {
      const wb = new Excel.Workbook();
      await wb.xlsx.load(buffer);
      // Sheet chi tiết theo template (tên có thể lệch dấu cách) — tìm theo từ khoá.
      const wsTmpl = wb.worksheets.find(w => /t[ổo]ng theo template/i.test(w.name)) || wb.getWorksheet('Tổng theo Template');
      if (!wsTmpl) throw new Error(this.$t('parseNoSheet'));

      // Xác định hàng tiêu đề (có "ID mẫu tin") để lấy đúng index cột.
      let headerRow = 0, col = {oa: 2, refId: 3, billable: 6, amount: 7};
      wsTmpl.eachRow((row, rn) => {
        if (headerRow) return;
        const cells = row.values.map(v => (v && typeof v === 'object' && v.result != null ? v.result : v));
        const joined = cells.map(c => String(c || '').toLowerCase()).join('|');
        if (joined.includes('id mẫu tin') || joined.includes('id mau tin')) {
          headerRow = rn;
          cells.forEach((c, idx) => {
            const s = String(c || '').toLowerCase();
            if (s.includes('tên oa') || s.includes('ten oa')) col.oa = idx;
            else if (s.includes('id mẫu') || s.includes('id mau')) col.refId = idx;
            else if (s.includes('tính phí') || s.includes('tinh phi')) col.billable = idx;
            else if (s.includes('thành tiền') || s.includes('thanh tien')) col.amount = idx;
          });
        }
      });

      const providerLines = [];
      wsTmpl.eachRow((row, rn) => {
        if (!headerRow || rn <= headerRow) return;
        const refId = this.cellStr(row.getCell(col.refId));
        const count = this.cellNum(row.getCell(col.billable));
        if (!refId && !count) return; // bỏ dòng trống/tổng
        providerLines.push({
          oaName: this.cellStr(row.getCell(col.oa)),
          templateRefId: refId,
          count,
          amount: this.cellNum(row.getCell(col.amount)),
        });
      });

      // Tổng phía provider: lấy CK/tặng/VAT/payable từ sheet DSC theo nhãn cột A.
      const providerTotals = {
        billableCount: providerLines.reduce((s, l) => s + l.count, 0),
        grossAmount: providerLines.reduce((s, l) => s + l.amount, 0),
        discount: 0, bonus: 0, vat: 0, payable: 0,
      };
      const wsDsc = wb.worksheets.find(w => /dsc/i.test(w.name));
      if (wsDsc) {
        // Khớp nhãn chính xác đầu chuỗi — tránh "Thành tiền sau chiết khấu"/"...sau tặng thêm"
        // (chứa "chiết khấu"/"tặng thêm") bị nhận nhầm. Chỉ lấy dòng nhãn gốc ở cột A.
        wsDsc.eachRow((row) => {
          const label = this.cellStr(row.getCell(1)).toLowerCase();
          const val = this.cellNum(row.getCell(2));
          if (!label) return;
          if (label === 'doanh thu') providerTotals.grossAmount = val;
          else if (label.startsWith('chiết khấu') || label.startsWith('chiet khau')) providerTotals.discount = val;
          else if (label.startsWith('giá trị tặng thêm') || label.startsWith('gia tri tang them')) providerTotals.bonus = val;
          else if (label === 'vat') providerTotals.vat = val;
          else if (label.startsWith('số tiền phải thanh toán') || label.startsWith('so tien phai thanh toan')) providerTotals.payable = val;
        });
      }
      // Fallback: nếu file không có dòng "phải thanh toán", ước theo gross - CK - tặng + VAT.
      if (!providerTotals.payable) {
        providerTotals.payable = providerTotals.grossAmount - providerTotals.discount - providerTotals.bonus + providerTotals.vat;
      }
      return {providerLines, providerTotals};
    },
    async drill(line) {
      if (!this.saved || !line.templateRefId) return;
      this.showDrill = true;
      this.drillLoading = true;
      this.drillItems = [];
      this.drillTitle = `${line.oaName} — ${line.templateRefId}`;
      try {
        const {data} = await this.$service.get(`/zns-reconcile/${this.saved._id}/detail`, {
          params: {templateRefId: line.templateRefId, pageSize: 200},
        });
        this.drillItems = data.data || [];
        this.drillCount = data.itemCount || 0;
      } catch (e) {
        this.$swal({text: 'Lỗi tải chi tiết', icon: 'error'});
      } finally {
        this.drillLoading = false;
      }
    },
  },
  i18n: {
    messages: {
      en: {
        pageTitle: 'ZBS reconciliation',
        period: 'Period', selectMonth: 'Select month', provider: 'Provider', allProviders: 'All providers',
        loadReport: 'Load report', importProviderFile: 'Import provider file',
        reconcileNote: 'Reconciliation groups billable ZBS by send time (matches provider "transaction date"). Cost/discount/VAT come from the provider file only.',
        pivotOnlyNote: 'DB figures only (quantity + revenue). Import the provider file to reconcile cost & margin.',
        systemRevenue: 'Callio revenue', providerPayable: 'Provider payable', grossMargin: 'Gross margin',
        marginNote: 'revenue − payable', qtyDiff: 'Qty diff', matched: 'Matched', needReview: 'Needs review',
        billableMsg: 'billable msg', oa: 'OA', templateId: 'Template ID',
        systemCount: 'Callio count', providerCount: 'Provider count', diff: 'Diff',
        systemRevenueShort: 'Revenue', providerOnly: 'Provider only', systemOnly: 'Callio only',
        viewDetail: 'View messages', total: 'Total', noData: 'No data for this period',
        phone: 'Phone', client: 'Client', sendTime: 'Send time', fee: 'Fee',
        showingFirst: 'Showing {0}/{1} messages', importSuccess: 'Imported & reconciled',
        parseNoSheet: 'File missing sheet "Tổng theo Template"', parseNoLines: 'No template rows parsed from file',
      },
      vi: {
        pageTitle: 'Đối soát ZBS',
        period: 'Kỳ', selectMonth: 'Chọn tháng', provider: 'Nhà cung cấp', allProviders: 'Tất cả nhà cung cấp',
        loadReport: 'Tải báo cáo', importProviderFile: 'Import file nhà cung cấp',
        reconcileNote: 'Đối soát gom tin ZBS tính phí theo thời điểm gửi (khớp "Ngày giao dịch" của nhà cung cấp). Giá vốn/chiết khấu/VAT chỉ lấy từ file nhà cung cấp.',
        pivotOnlyNote: 'Chỉ số liệu từ DB (sản lượng + doanh thu). Import file nhà cung cấp để đối soát giá vốn & lợi nhuận.',
        systemRevenue: 'Doanh thu Callio', providerPayable: 'Phải trả NCC', grossMargin: 'Lợi nhuận gộp',
        marginNote: 'doanh thu − phải trả', qtyDiff: 'Lệch sản lượng', matched: 'Khớp', needReview: 'Cần rà soát',
        billableMsg: 'tin tính phí', oa: 'OA', templateId: 'ID mẫu tin',
        systemCount: 'Callio ghi nhận', providerCount: 'NCC tính', diff: 'Lệch',
        systemRevenueShort: 'Doanh thu', providerOnly: 'Chỉ có ở NCC', systemOnly: 'Chỉ có ở Callio',
        viewDetail: 'Xem chi tiết tin', total: 'Tổng', noData: 'Không có dữ liệu trong kỳ',
        phone: 'Số điện thoại', client: 'Khách hàng', sendTime: 'Thời điểm gửi', fee: 'Phí',
        showingFirst: 'Hiển thị {0}/{1} tin', importSuccess: 'Đã import & đối soát',
        parseNoSheet: 'File thiếu sheet "Tổng theo Template"', parseNoLines: 'Không đọc được dòng mẫu tin nào từ file',
      },
    },
  },
};
</script>

<style scoped>
.bg-danger-soft { background-color: rgba(220, 53, 69, 0.08); }
</style>
